// client/src/pages/secure/LeasingResponderPage.js — staff test console for the PM leasing
// responder. Ask what a prospect would ask; see the answer, how it was read and why.
// The answers come from the unit record and the PUBLISHED screening criteria; they never
// decide or predict eligibility, never discuss protected characteristics, and always
// offer a person. Every answer is logged.
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { Card, Empty, ErrorLine, Btn, Tabs, fmtDateTime, input } from '../../components/secure/territory/Shared';

const SAMPLES = ['Is it still available?', 'How much is the rent?', 'Do you take Section 8?', 'What are the requirements?', 'Will I get approved with a 580 credit score?',
    'Can I bring my emotional support dog?', 'Do you allow kids?', 'Is it a safe neighborhood?', 'Can I see it Saturday afternoon?'];
const FLAG_TEXT = { fair_housing_decline: 'Declined: fair housing', no_prediction: 'No eligibility prediction', route_to_human: 'Routed to a person',
    accommodation_request: 'Accommodation request', source_of_income: 'Source of income', fair_housing_familial: 'Familial status', offer_showing: 'Showing offered' };

const Console = ({ units, disclosure }) => {
    const api = useApiClient();
    const [unitId, setUnitId] = useState('');
    const [q, setQ] = useState('');
    const [turns, setTurns] = useState([]);
    const [error, setError] = useState('');
    const [show, setShow] = useState({ name: '', email: '', phone: '', time: '' });
    const ask = async (question) => {
        if (!unitId || !question.trim()) return;
        try { const a = await api.post('/api/leasing-responder/ask', { unit_id: unitId, question }); setTurns((t) => [...t, { q: question, a }]); setQ(''); setError(''); }
        catch (e) { setError(e.message); }
    };
    const requestShowing = async () => {
        try {
            const r = await api.post('/api/leasing-responder/showing-requests', { unit_id: unitId, name: show.name, email: show.email, phone: show.phone,
                preferred_times: show.time ? [new Date(show.time).toISOString()] : [] });
            setTurns((t) => [...t, { q: '(showing request)', a: { answer: r.message, intent: 'showing', flags: ['task created'] } }]); setError('');
        } catch (e) { setError(e.message); }
    };
    return (
        <div className="space-y-3">
            <select className={input} value={unitId} onChange={(e) => { setUnitId(e.target.value); setTurns([]); }}>
                <option value="">Choose a unit (vacant, on notice or being turned)…</option>
                {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.name || u.street_address} — unit {u.unit_number} ({u.status}, {u.state})</option>)}
            </select>
            {unitId && <>
                <p className="text-xs text-slate-500">Shown to the prospect first: “{disclosure}”</p>
                <div className="space-y-3">{turns.map((t, i) => (
                    <div key={i} className="space-y-1">
                        <div className="text-sm text-right"><span className="inline-block bg-slate-800 text-white rounded-lg px-3 py-1.5">{t.q}</span></div>
                        <div className="text-sm"><span className="inline-block bg-slate-100 rounded-lg px-3 py-2 whitespace-pre-wrap">{t.a.answer}</span></div>
                        <div className="text-xs text-slate-500">read as <strong>{t.a.intent}</strong>{t.a.jurisdiction ? ` · ${t.a.jurisdiction} rules` : ''}{t.a.criteria_version ? ` · criteria ${t.a.criteria_version}` : ''}
                            {(t.a.flags || []).length ? ` · ${(t.a.flags || []).map((f) => FLAG_TEXT[f] || f).join(' · ')}` : ''}</div>
                    </div>))}</div>
                <div className="flex gap-2"><input className={input} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && ask(q)} placeholder="Ask as a prospect would…" /><Btn onClick={() => ask(q)}>Ask</Btn></div>
                <div className="flex flex-wrap gap-1">{SAMPLES.map((s) => <button key={s} type="button" className="text-xs px-2 py-1 bg-slate-100 rounded hover:bg-slate-200" onClick={() => ask(s)}>{s}</button>)}</div>
                <Card title="Test a showing request">
                    <div className="grid md:grid-cols-4 gap-2">
                        <input className={input} placeholder="Name" value={show.name} onChange={(e) => setShow({ ...show, name: e.target.value })} />
                        <input className={input} placeholder="Email" value={show.email} onChange={(e) => setShow({ ...show, email: e.target.value })} />
                        <input className={input} placeholder="Phone" value={show.phone} onChange={(e) => setShow({ ...show, phone: e.target.value })} />
                        <input type="datetime-local" className={input} value={show.time} onChange={(e) => setShow({ ...show, time: e.target.value })} />
                    </div>
                    <div className="mt-2"><Btn kind="secondary" onClick={requestShowing}>Request showing (creates a task for a person)</Btn></div>
                </Card>
            </>}
            <ErrorLine error={error} />
        </div>
    );
};

const Log = () => {
    const api = useApiClient();
    const [rows, setRows] = useState([]);
    const [error, setError] = useState('');
    useEffect(() => { api.get('/api/leasing-responder/log').then(setRows).catch((e) => setError(e.message)); }, [api]);
    return (
        <div className="space-y-2"><ErrorLine error={error} />
            {rows.length === 0 ? <Empty>No answers yet.</Empty> : <ul className="divide-y bg-white rounded shadow text-sm">{rows.map((r) => (
                <li key={r.log_id} className="p-2"><div className="text-xs text-slate-500">{fmtDateTime(r.created_at)} · unit #{r.unit_id} · {r.channel} · {r.intent}{r.flags ? ` · ${r.flags}` : ''}{Number(r.used_llm) ? ' · rephrased' : ''}</div>
                    <div><strong>Q:</strong> {r.question}</div><div className="whitespace-pre-wrap text-slate-700"><strong>A:</strong> {r.answer}</div></li>))}</ul>}
        </div>
    );
};

const LeasingResponderPage = () => {
    const api = useApiClient();
    const [tab, setTab] = useState('console');
    const [data, setData] = useState({ units: [], disclosure: '' });
    const [error, setError] = useState('');
    const load = useCallback(() => api.get('/api/leasing-responder/units').then(setData).catch((e) => setError(e.message)), [api]);
    useEffect(() => { load(); }, [load]);
    return (
        <div className="p-4 md:p-6 space-y-4">
            <div><h1 className="text-2xl font-bold text-slate-800">Leasing Responder — test console</h1>
                <p className="text-sm text-slate-600">The automated answers prospects will get about a vacant unit, from the unit record and the published screening criteria. It never says whether anyone will qualify, never discusses protected characteristics or the neighbourhood's people, follows the property's state on source of income, and always offers a person. It works with no AI service configured.</p></div>
            <ErrorLine error={error} />
            <Tabs value={tab} onChange={setTab} tabs={[['console', 'Console'], ['log', 'Answer log']]} />
            {tab === 'console' ? <Console units={data.units} disclosure={data.disclosure} /> : <Log />}
        </div>
    );
};

export default LeasingResponderPage;
